// src/pages/Error/ErrorPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBug,
  FaExclamationTriangle,
  FaHome,
  FaRedo,
  FaShieldAlt,
  FaSignInAlt,
} from "react-icons/fa";

import backgroundImage from "./HeaderSlider_02.jpg";
import { getSettings } from "./../../services/settingsServices";
import { toAbsolutePhotoUrl } from "./../../services/settingsPhotoServices";
import useScrollToTop from "./../../hooks/useScollToTop";

const DEFAULT_SETTINGS = {
  nom_club: "MonAppliClub",
  logo_club: "",
  header_url1: "",
  header_url2: "",
  header1_photo_url: "",
  header2_photo_url: "",
  couleur_principale: "",
  primary_color: "",
};

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getFriendlyErrorMessage(rawError) {
  if (!rawError) {
    return "Une erreur inconnue est survenue.";
  }

  const value = String(rawError).trim();

  const knownErrors = {
    unauthorized: "Vous devez être connecté pour accéder à cette page.",
    forbidden: "Vous n’avez pas les droits nécessaires pour accéder à cette page.",
    not_found: "La page demandée est introuvable.",
    server_error:
      "Le serveur rencontre actuellement un problème. Veuillez réessayer dans quelques instants.",
    expired_token:
      "Votre session a expiré. Veuillez vous reconnecter pour continuer.",
  };

  return knownErrors[value] || value;
}

function ErrorPage() {
  useScrollToTop();

  const navigate = useNavigate();
  const location = useLocation();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const error = getFriendlyErrorMessage(params.get("error"));

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted || !clubSettings) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...clubSettings,
        });

        const primaryColor =
          clubSettings.couleur_principale || clubSettings.primary_color;

        if (primaryColor) {
          document.documentElement.style.setProperty(
            "--primary-color",
            primaryColor
          );
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const logoUrl = useMemo(() => {
    const logo = toAbsolutePhotoUrl(settings.logo_club?.trim() || "");
    return logo || "/img/logo_club_512.png";
  }, [settings.logo_club]);

  const backgroundUrl = useMemo(() => {
    const candidate =
      settings.header_url2 ||
      settings.header_url1 ||
      settings.header2_photo_url ||
      settings.header1_photo_url;

    return toAbsolutePhotoUrl(candidate?.trim?.() || "") || backgroundImage;
  }, [
    settings.header_url1,
    settings.header_url2,
    settings.header1_photo_url,
    settings.header2_photo_url,
  ]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/");
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url("${backgroundUrl}")`,
        }}
      />

      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/94 via-slate-950/76 to-slate-950/62" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />

      <div className="absolute left-[-130px] top-24 h-80 w-80 rounded-full bg-primary/25 blur-3xl" />
      <div className="absolute bottom-[-140px] right-[-100px] h-96 w-96 rounded-full bg-red-500/20 blur-3xl" />
      <div className="absolute right-16 top-20 hidden h-44 w-44 rounded-full bg-blue-500/20 blur-3xl sm:block" />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="w-full max-w-5xl">
          <div className="mb-6 flex items-center justify-between">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-bold text-white/80 backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white hover:text-slate-950"
            >
              <FaArrowLeft className="text-xs" />
              Retour
            </button>

            <Link
              to="/"
              className="hidden items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-bold text-white/80 backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white hover:text-slate-950 sm:inline-flex"
            >
              <FaHome className="text-xs" />
              Accueil
            </Link>
          </div>

          <section className="grid overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/10 shadow-2xl backdrop-blur-2xl lg:grid-cols-[0.9fr_1.1fr]">
            <aside className="relative hidden overflow-hidden bg-slate-950/75 p-10 lg:block">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(239,68,68,0.30),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.20),transparent_36%)]" />

              <div className="relative flex h-full flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/70 backdrop-blur-xl">
                    <FaShieldAlt className="text-primary" />
                    Espace sécurisé
                  </div>

                  <h1 className="mt-7 font-barlow text-5xl font-bold leading-tight text-white">
                    Une erreur est survenue.
                  </h1>

                  <p className="mt-5 max-w-md text-base leading-8 text-white/70">
                    La page demandée n’a pas pu être affichée correctement. Vous
                    pouvez revenir en arrière, retourner à l’accueil ou tenter
                    de recharger la page.
                  </p>
                </div>

                <div className="rounded-[2rem] border border-white/10 bg-white/10 p-5 backdrop-blur-xl">
                  <div className="flex items-center gap-4">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white p-2 shadow-xl">
                      <img
                        src={logoUrl}
                        alt={settings.nom_club || "MonAppliClub"}
                        className="h-full w-full object-contain"
                        draggable="false"
                        onError={(event) => {
                          event.currentTarget.src = "/img/logo_club_512.png";
                        }}
                      />
                    </div>

                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45">
                        Site officiel
                      </p>

                      <p className="mt-1 truncate font-barlow text-2xl font-bold text-white">
                        {settings.nom_club || "MonAppliClub"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            <div className="relative bg-white p-6 text-slate-950 dark:bg-slate-900 dark:text-white sm:p-8 lg:p-10">
              <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-red-500/10 blur-3xl" />

              <div className="relative mx-auto max-w-md">
                <div className="mb-8 text-center">
                  <div className="mx-auto flex h-24 w-24 items-center justify-center overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-xl dark:border-white/10">
                    <img
                      src={logoUrl}
                      alt={settings.nom_club || "MonAppliClub"}
                      className="h-full w-full object-contain"
                      draggable="false"
                      onError={(event) => {
                        event.currentTarget.src = "/img/logo_club_512.png";
                      }}
                    />
                  </div>

                  <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                    <FaExclamationTriangle />
                    Erreur
                  </div>

                  <h2 className="mt-5 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white">
                    Oups, impossible de continuer
                  </h2>

                  <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Une erreur empêche l’affichage de cette page sur{" "}
                    <span className="font-bold text-slate-700 dark:text-slate-200">
                      {settings.nom_club || "MonAppliClub"}
                    </span>
                    .
                  </p>
                </div>

                <div className="rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-left text-sm leading-6 text-red-800 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white text-red-600 shadow-sm dark:bg-slate-950">
                      <FaBug />
                    </span>

                    <div className="min-w-0">
                      <p className="font-bold">Message d’erreur</p>

                      <p className="mt-1 break-words text-red-700/90 dark:text-red-200/90">
                        {error}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={handleBack}
                    className={cn(
                      "inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-5 text-sm font-bold transition",
                      "bg-primary text-white shadow-lg shadow-primary/20 hover:-translate-y-0.5 hover:brightness-110",
                      "focus:outline-none focus:ring-4 focus:ring-primary/20"
                    )}
                  >
                    <FaArrowLeft />
                    Revenir en arrière
                  </button>

                  <button
                    type="button"
                    onClick={handleReload}
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-white/10"
                  >
                    <FaRedo />
                    Réessayer
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Link
                    to="/"
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-5 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:bg-slate-100 dark:border-white/10 dark:bg-slate-950/70 dark:text-slate-200 dark:hover:bg-white/10"
                  >
                    <FaHome />
                    Accueil
                  </Link>

                  <Link
                    to="/login"
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-5 text-sm font-bold text-orange-700 transition hover:-translate-y-0.5 hover:bg-orange-100 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300 dark:hover:bg-orange-500/20"
                  >
                    <FaSignInAlt />
                    Connexion
                  </Link>
                </div>

                <p className="mt-8 text-center text-xs leading-5 text-slate-400">
                  Si le problème persiste, contactez le club ou
                  l’administrateur de l’application.
                </p>
              </div>
            </div>
          </section>

          <p className="mt-5 text-center text-xs font-semibold text-white/70">
            MonAppliClub • Gestion de club simplifiée
          </p>
        </div>
      </div>
    </main>
  );
}

export default ErrorPage;